/**
 * Read a PNG's pixel size from its header, without decoding the image.
 *
 * A PNG starts with an 8-byte signature followed by the IHDR chunk: a 4-byte
 * length, the ASCII type `IHDR`, then width and height as big-endian 32-bit
 * integers. Only those first 24 bytes are read, so a 25 MB file costs the same
 * as a tiny one and nothing is ever painted.
 *
 * Returns `null` for anything that is not a well-formed PNG header (a JPEG
 * renamed `.png`, a truncated file, a zero size). The caller treats `null` as
 * "could not read this file's size"; the server re-checks the content type at
 * mint regardless.
 */

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] as const;
const HEADER_BYTES = 24;

export interface PngDimensions {
  width: number;
  height: number;
}

function readBytes(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsArrayBuffer(blob);
  });
}

export async function readPngDimensions(file: Blob): Promise<PngDimensions | null> {
  let buffer: ArrayBuffer;
  try {
    buffer = await readBytes(file.slice(0, HEADER_BYTES));
  } catch {
    return null;
  }
  if (buffer.byteLength < HEADER_BYTES) return null;

  const view = new DataView(buffer);
  for (let i = 0; i < PNG_SIGNATURE.length; i += 1) {
    if (view.getUint8(i) !== PNG_SIGNATURE[i]) return null;
  }
  // Chunk type at bytes 12..15 must be IHDR (the first chunk of every PNG).
  const type = String.fromCharCode(view.getUint8(12), view.getUint8(13), view.getUint8(14), view.getUint8(15));
  if (type !== 'IHDR') return null;

  const width = view.getUint32(16, false);
  const height = view.getUint32(20, false);
  if (width === 0 || height === 0) return null;
  return { width, height };
}
